import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { Newspaper, ArrowRight, CalendarDays, Eye, Layers } from "lucide-react";

const PAGE = 12;

export default function RevuePresseList() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Revues de presse quotidiennes — Chartres & Eure-et-Loir | PROXIreduc-28",
    description: "Toutes les éditions quotidiennes de la revue de presse locale de Chartres et de l'Eure-et-Loir : l'essentiel de l'actualité du jour, résumé et sourcé.",
    canonicalPath: "/revues-de-presse",
    image: "/og-blog.jpg",
  });

  useEffect(() => {
    setLoading(true);
    api.get(`/revue-presse?limit=${PAGE}&skip=${skip}`)
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .finally(() => setLoading(false));
  }, [skip]);

  const pages = Math.ceil(total / PAGE);
  const current = Math.floor(skip / PAGE);

  return (
    <div data-testid="revue-list-page">
      <section className="bg-[#172A3A] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F47B20]">
            <Layers size={14} /> Rendez-vous quotidien
          </div>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold">
            Revues de presse Chartres & Eure-et-Loir
          </h1>
          <p className="mt-3 text-base text-slate-300 max-w-2xl">
            Chaque jour, l'essentiel de l'actualité locale réuni en une seule édition : à la une du jour,
            résumés et liens vers les sources. Retrouvez toutes nos éditions ci-dessous.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="text-slate-500">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="revue-empty">
            La première revue de presse arrive très bientôt. Revenez demain matin !
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="revue-grid">
              {items.map((p, idx) => (
                <Link key={p.id} to={`/revue-presse/${p.slug}`} data-testid={`revue-card-${p.slug}`}
                  className={`group bg-white border rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all ${idx === 0 && current === 0 ? "border-[#F47B20] ring-2 ring-orange-100 sm:col-span-2 lg:col-span-1" : "border-slate-200"}`}>
                  <div className="aspect-[1.91/1] bg-slate-100 overflow-hidden relative">
                    {p.cover_image
                      ? <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      : <div className="w-full h-full grid place-items-center text-[#F47B20]/40"><Newspaper size={40} /></div>}
                    {idx === 0 && current === 0 && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#F47B20] text-white text-[11px] font-bold uppercase tracking-wide">Édition du jour</span>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                      {p.digest_date && <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> {new Date(p.digest_date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>}
                      {p.article_count > 0 && <span className="inline-flex items-center gap-1 text-[#E05638] font-semibold"><Newspaper size={12} /> {p.article_count} articles</span>}
                    </div>
                    <h2 className="mt-2 font-heading text-lg font-bold text-slate-900 group-hover:text-[#E05638] transition-colors line-clamp-2">{p.title}</h2>
                    {p.excerpt && <p className="mt-1.5 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>}
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#E05638]">Lire la revue <ArrowRight size={14} /></span>
                  </div>
                </Link>
              ))}
            </div>

            {pages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2" data-testid="revue-pagination">
                {Array.from({ length: pages }).map((_, i) => (
                  <button key={i} onClick={() => setSkip(i * PAGE)} data-testid={`revue-page-${i + 1}`}
                    className={`w-10 h-10 rounded-xl text-sm font-bold transition-colors ${i === current ? "bg-[#E05638] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
